import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import { getData, postData } from "../../ServerRequest";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GridViewIcon from "@mui/icons-material/GridView";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const buttonStyle = {
  background: "#f8f9fa",
  border: "3px solid #b0bec5",
  color: "black",
  fontSize: "0.850rem",
  "&:hover": { background: "#eceff1" },
  fontFamily: "Inter, sans-serif",
};

const sectionStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.6)",
  marginBottom: 2,
  boxShadow: "0px 4px 8px rgba(0, 0, 1, 0.1)",
  fontFamily: "Inter, sans-serif",
};

const inputStyle = {
  "& .MuiInputBase-root": {
    height: 35,
    borderRadius: 0,
    backgroundColor: "white",
    fontFamily: "Inter, sans-serif",
  },
};

export default function LandingFooter() {
  const [footerColumns, setFooterColumns] = useState([]);
  const [loading, setLoading] = useState();
  const [editIndex, setEditIndex] = useState({});

  // Move fetchFooterData outside useEffect so it can be reused
  const fetchFooterData = async () => {
    let resData = await getData("/api/theme/footers");
    console.log(resData);
    let footer_data = resData["data"]["footers"];
    if (footer_data) {
      // Map backend fields to frontend state
      setFooterColumns(
        footer_data.map((col, idx) => ({
          id: col.id,
          title: col.title,
          position: col.position,
          status: col.status,
          links: col.links.map((link, lidx) => ({
            label: link.label,
            url: link.url === null ? "" : link.url, // Fallback for null url
            is_external:
              typeof link.is_external === "boolean" ? link.is_external : false,
            position:
              link.position !== undefined ? Number(link.position) : lidx + 1,
            status: typeof link.status === "boolean" ? link.status : true,
            column:
              link.column !== undefined
                ? Number(link.column)
                : col.position !== undefined
                ? Number(col.position)
                : idx + 1,
          })),
        }))
      );
    } else {
      console.error("Error fetching footer data:", resData.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  const handleFooterSubmit = async (index) => {
    let footer = footerColumns[index];
    let footerReqData = {
      'id': footer.id ? Number(footer.id) : null,
      'column_title': footer.title ? footer.title : "",
      'position': footer.position !== undefined ? Number(footer.position) : index + 1,
      'status': typeof footer.status === "boolean" ? footer.status : true,
      'links': footer.links.map((link, linkIdx) => ({
        label: link.label ? link.label : "",
        url: link.url && link.url.trim() !== "" ? link.url : null,
        is_external:
          typeof link.is_external === "boolean" ? link.is_external : false,
        position:
          link.position !== undefined ? Number(link.position) : linkIdx + 1,
        status: typeof link.status === "boolean" ? link.status : true,
        column: footer.id ? Number(footer.id) : null, // Ensure column is set correctly
      })),
    };

    let footerResData = await postData("/api/theme/footers", footerReqData);
    if (
      footerResData["data"]["code"] === "200" ||
      footerResData["data"]["code"] === 200
    ) {
      alert("Footer data saved successfully");
      setLoading(false);
      // Refresh data from backend after save
      await fetchFooterData();
      return true;
    } else {
      alert("Error saving footer data");
      setLoading(false);
    }
    return false;
  };

  const handleTitleChange = (e, columnId) => {
    setFooterColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === columnId ? { ...column, title: e.target.value } : column
      )
    );
  };

  const handleLinkChange = (columnId, index, key, value) => {
    setFooterColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === columnId
          ? {
              ...column,
              links: column.links.map((link, i) =>
                i === index ? { ...link, [key]: value } : link
              ),
            }
          : column
      )
    );
  };

  const handleAddLink = (columnId) => {
    setFooterColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === columnId
          ? {
              ...column,
              links: [
                ...column.links,
                {
                  label: "",
                  url: "",
                  is_external: false,
                  position: column.links.length + 1,
                  status: true,
                  column: Number(column.id),
                },
              ],
            }
          : column
      )
    );
  };

  const handleDeleteLink = (columnId, index) => {
    setFooterColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === columnId
          ? { ...column, links: column.links.filter((_, i) => i !== index) }
          : column
      )
    );
  };

  const handleTitleEditClick = (columnId) => {
    setEditIndex((prevState) => ({
      ...prevState,
      [columnId]: {
        ...prevState[columnId],
        title: prevState[columnId]?.title ? false : true,
      },
    }));
  };

  const handleLinkEditClick = (columnId, index) => {
    setEditIndex((prevState) => ({
      ...prevState,
      [columnId]: prevState[columnId] === index ? -1 : index,
    }));
  };

  const handleAddAccordion = () => {
    const newId = footerColumns.length + 1;
    setFooterColumns((prevColumns) => [
      ...prevColumns,
      {
        id: newId,
        title: `New Column ${newId}`,
        position: prevColumns.length + 1,
        status: true,
        links: [
          {
            label: "",
            url: "",
            is_external: false,
            position: 1,
            status: true,
            column: newId,
          },
        ],
      },
    ]);
  };

  return (
    <Box sx={{ width: "95%", paddingLeft: 3 }}>
      {footerColumns.map((column, colIndex) => (
        <Accordion key={column.id} sx={sectionStyle}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton component="icon">
                <GridViewIcon />
              </IconButton>
              <Typography variant="h7">{`Column ${colIndex + 1}`}</Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{ backgroundColor: "#eceff1", padding: 4, borderRadius: 0 }}
            >
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Column Title
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
              >
                <TextField
                  fullWidth
                  sx={{ marginBottom: 2, ...inputStyle }}
                  value={column.title}
                  onChange={(e) => handleTitleChange(e, column.id)}
                  disabled={!editIndex[column.id]?.title}
                />
                <IconButton
                  sx={{ marginBottom: 2, color: "grey" }}
                  onClick={() => handleTitleEditClick(column.id)}
                  aria-label="edit-title"
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Box>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Links
              </Typography>
              {column.links.map((link, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    marginBottom: 2,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex-col",
                      alignItems: "center",
                      marginRight: 2,
                      width: "50%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#424956",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      Label
                    </Typography>
                    <TextField
                      fullWidth
                      sx={{ marginRight: 2, ...inputStyle }}
                      value={link.label}
                      onChange={(e) =>
                        handleLinkChange(
                          column.id,
                          index,
                          "label",
                          e.target.value
                        )
                      }
                      disabled={editIndex[column.id] !== index}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex-col",
                      alignItems: "center",
                      marginRight: 2,
                      width: "50%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#424956",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      URL
                    </Typography>
                    <TextField
                      fullWidth
                      sx={{ marginRight: 2, ...inputStyle }}
                      value={link.url}
                      onChange={(e) =>
                        handleLinkChange(
                          column.id,
                          index,
                          "url",
                          e.target.value
                        )
                      }
                      disabled={editIndex[column.id] !== index}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex-col",
                      alignItems: "center",
                      marginRight: 2,
                      width: "10%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#424956",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      Position
                    </Typography>
                    <TextField
                      fullWidth
                      sx={{ marginRight: 2, ...inputStyle }}
                      value={link.position}
                      onChange={(e) =>
                        handleLinkChange(
                          column.id,
                          index,
                          "position",
                          e.target.value
                        )
                      }
                      disabled={editIndex[column.id] !== index}
                    />
                  </Box>
                  <IconButton
                    sx={{ color: "grey" }}
                    onClick={() => handleLinkEditClick(column.id, index)}
                    aria-label="edit"
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: "red" }}
                    onClick={() => handleDeleteLink(column.id, index)}
                    aria-label="delete"
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                </Box>
              ))}
              <IconButton
                variant="contained"
                sx={{ ...buttonStyle, marginRight: 3, marginTop: 2 }}
                onClick={() => handleAddLink(column.id)}
                aria-label="add-link"
              >
                <AddIcon />
              </IconButton>
              <IconButton
                variant="contained"
                sx={{ ...buttonStyle, marginRight: 3, marginTop: 2 }}
                onClick={() => handleFooterSubmit(colIndex)}
                aria-label="save-changes"
              >
                <SaveOutlinedIcon />
              </IconButton>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      {/* Button to add a new accordion section */}
      <IconButton
        variant="contained"
        sx={{ ...buttonStyle, marginTop: 3 }}
        onClick={handleAddAccordion}
        aria-label="add"
      >
        <AddIcon />
      </IconButton>
    </Box>
  );
}
